import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import { IdentityProviderCompanyContainer } from "@containers/identity-provider-details/identity-provider-company-container/identity-provider-company-container";
import { IdentityProviderCustomUrlParametersContainer } from "@containers/identity-provider-details/identity-provider-custom-url-parameters-container/identity-provider-custom-url-parameters-container";
import { IdentityProviderFederatedAttributesContainer } from "@containers/identity-provider-details/identity-provider-federated-attributes-container/identity-provider-federated-attributes-container";
import { IdentityProviderInfoContainer } from "@containers/identity-provider-details/identity-provider-info-container/identity-provider-info-container";
import { IdentityProviderDetailsTabTypes } from "@enums/identity-provider-details-tab-types";
import { IdentityProviderDetailsReferenceData } from "@idw-types/state/identity-provider-details-state";
import { FormattedIdentityProviderDetails } from "@idw-types/data/identity-providers/identity-provider-details";
import { FormattedIdentityProviderCompanyIDPInfoParam, FormattedIdentityProviderCompanyIDPAttributeMap } from "@idw-types/data/identity-providers";
import { LabelWidthTypes } from "@enums/label-width-types";
import { IdentityProviderValidationErrors } from "@idw-types/common/validation/identity-provider/identity-provider-validation-errors";
import { useMemo } from "react";

export type IdentityProviderDetailsTabContainerProps = {
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    identityProviderReferenceData: IdentityProviderDetailsReferenceData;
    identityProviderDetailsState: FormattedIdentityProviderDetails;
    validationErrors: IdentityProviderValidationErrors;
    setIdentityProviderDetailsState: (identityProviderDetailsState: FormattedIdentityProviderDetails) => void;
}

export const IdentityProviderDetailsTabContainer = (props: IdentityProviderDetailsTabContainerProps) => {
    const {
        labelWidth,
        isDisabled,
        identityProviderReferenceData,
        identityProviderDetailsState,
        validationErrors,
        setIdentityProviderDetailsState
    } = props;

    const { categories, schemes, companies, protocols, attributeTypeFields, urlTypes, attributeTypes } = identityProviderReferenceData;


    const setCompanyIDPInfoParamsState = (newCompanyIDPInfoParams: Array<FormattedIdentityProviderCompanyIDPInfoParam>) => {
        setIdentityProviderDetailsState({
            ...identityProviderDetailsState,
            companyIDPInfo: {
                ...identityProviderDetailsState.companyIDPInfo,
                companyIDPInfoParams: newCompanyIDPInfoParams
            }
        });
    };

    const setCompanyIDPAttributeMapsState = (newCompanyIDPAttributeMaps: Array<FormattedIdentityProviderCompanyIDPAttributeMap>) => {
        setIdentityProviderDetailsState({
            ...identityProviderDetailsState,
            companyIDPInfo: {
                ...identityProviderDetailsState.companyIDPInfo,
                companyIDPAttributeMaps: newCompanyIDPAttributeMaps
            }
        });
    };
    
    const allowedCompanies = useMemo(() => {
        if (companies) {
            return identityProviderReferenceData.companies.filter(company => company.type === "RP");
        }

        return [];
    }, [companies]);

    return (<TabContainer>
        {/* @ts-ignore */}
        <Tabs id="identity-provider-details" fill={true} unmountOnExit={true}>
            <Tab eventKey={IdentityProviderDetailsTabTypes.DETAILS} title={validationErrors.generalInfo.tabTitle}>
                <IdentityProviderInfoContainer
                    categories={categories}
                    companies={allowedCompanies}
                    protocols={protocols}
                    isDisabled={isDisabled}
                    state={identityProviderDetailsState}
                    validationErrors={validationErrors.generalInfo}
                    labelWidth={labelWidth}
                    setState={setIdentityProviderDetailsState}
                />
            </Tab>
            <Tab eventKey={IdentityProviderDetailsTabTypes.COMPANY} title={validationErrors.companyIDP.tabTitle}>
                <IdentityProviderCompanyContainer
                    schemes={schemes}
                    isDisabled={isDisabled}
                    validationErrors={validationErrors.companyIDP}
                    state={identityProviderDetailsState}
                    labelWidth={labelWidth}
                    setState={setIdentityProviderDetailsState}
                />
            </Tab>
            <Tab eventKey={IdentityProviderDetailsTabTypes.URL_PARAMETERS} title={validationErrors.customURLParameters.tabTitle}>
                <IdentityProviderCustomUrlParametersContainer
                    urlTypes={urlTypes}
                    isDisabled={isDisabled}
                    validationErrors={validationErrors.customURLParameters}
                    companyIDPInfoParamsState={identityProviderDetailsState.companyIDPInfo.companyIDPInfoParams}
                    setCompanyIDPInfoParamsState={setCompanyIDPInfoParamsState}
                />
            </Tab>
            <Tab eventKey={IdentityProviderDetailsTabTypes.FEDERATED_ATTRIBUTES} title={validationErrors.federatedAttributes.tabTitle}>
                <IdentityProviderFederatedAttributesContainer
                    attributeTypes={attributeTypes}
                    attributeTypeFields={attributeTypeFields}
                    isDisabled={isDisabled}
                    validationErrors={validationErrors.federatedAttributes}
                    companyIDPAttributeMapsState={identityProviderDetailsState.companyIDPInfo.companyIDPAttributeMaps}
                    setCompanyIDPAttributeMapsState={setCompanyIDPAttributeMapsState}
                />
            </Tab>
        </Tabs>
    </TabContainer>);
}